import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Stage, Layer, Image as KonvaImage } from 'react-konva';

const MIN_SCALE = 0.1;
const MAX_SCALE = 10;
const SCALE_STEP = 1.1;

const checkerBg = `
  repeating-conic-gradient(#1e1e2e 0% 25%, #16162a 0% 50%) 0 0 / 20px 20px
`;

/**
 * CanvasArea renders a Konva Stage with zoom (mouse wheel) support.
 * Drag is disabled — only zoom via scroll wheel.
 * Shows a checkered background for transparency and a nice empty state.
 *
 * @param {{ imageSrc: string|null, loading: boolean, onUpload: Function, children: React.ReactNode }} props
 */
export default function CanvasArea({ imageSrc, loading, onUpload, children }) {
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [image, setImage] = useState(null);

  // Measure container size
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setDimensions({ width, height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Load image from URL
  useEffect(() => {
    if (!imageSrc) {
      setImage(null);
      return;
    }
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => setImage(img);
    img.onerror = () => setImage(null);
    img.src = imageSrc;
  }, [imageSrc]);

  // Zoom via mouse wheel
  const handleWheel = useCallback((e) => {
    e.evt.preventDefault();
    const stage = e.target.getStage();
    const pointer = stage.getPointerPosition();
    const oldScale = stage.scaleX();
    const direction = e.evt.deltaY < 0 ? 1 : -1;
    const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, direction > 0 ? oldScale * SCALE_STEP : oldScale / SCALE_STEP));

    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };

    setScale(newScale);
    setPosition({
      x: pointer.x - mousePointTo.x * newScale,
      y: pointer.y - mousePointTo.y * newScale,
    });
  }, []);

  // Center image offset
  const imgX = image ? (dimensions.width - image.width) / 2 : 0;
  const imgY = image ? (dimensions.height - image.height) / 2 : 0;

  return (
    <div
      ref={containerRef}
      style={{
        flex: 1,
        background: checkerBg,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <Stage
        width={dimensions.width}
        height={dimensions.height}
        scaleX={scale}
        scaleY={scale}
        x={position.x}
        y={position.y}
        onWheel={handleWheel}
      >
        <Layer>
          {image && <KonvaImage image={image} x={imgX} y={imgY} />}
        </Layer>
        {children}
      </Stage>

      {/* Loading spinner overlay */}
      {loading && (
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(15,15,26,0.6)', zIndex: 10,
        }}>
          <div style={{
            width: 40, height: 40, border: '3px solid #333',
            borderTop: '3px solid #667eea', borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Empty state */}
      {!imageSrc && !loading && (
        <div
          onClick={onUpload}
          style={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            color: '#555', fontSize: 14, textAlign: 'center',
            cursor: 'pointer', padding: 32, borderRadius: 12,
            border: '2px dashed #333', background: 'rgba(26,26,46,0.6)',
            transition: 'border-color 0.2s',
          }}
          onMouseEnter={(e) => e.currentTarget.style.borderColor = '#667eea'}
          onMouseLeave={(e) => e.currentTarget.style.borderColor = '#333'}
        >
          <div style={{ fontSize: 48, marginBottom: 12 }}>📁</div>
          <div style={{ color: '#888', fontSize: 15, marginBottom: 4 }}>Drop an image or click Upload</div>
          <div style={{ color: '#555', fontSize: 12 }}>Supports JPG, PNG, WebP</div>
        </div>
      )}
    </div>
  );
}
